// @flow

import React, {useState} from 'react';
import CustomTextField from '../../../../../elements/CustomTextField/CustomTextField';
import {Button, makeStyles} from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';

import {colors} from '../../../../../assets/styles/colors';
import CropModal from '../../../../../elements/CropModal';

import type {Theme} from '@material-ui/core';
import type {ProductVariantOptionValue} from '../../../../../entities';
import ColorOptionModal from '../ColorOptionModal/ColorOptionModal';
import {AddPhotoAlternateOutlined, ColorizeOutlined} from '@material-ui/icons';
import {classNames} from '../../../../../utils/react';

const useStyles = makeStyles((theme: Theme) => ({
  wrap: {
    border: `1px solid ${colors.grey300}`,
    padding: theme.spacing(1.5),
    borderRadius: '8px',
  },
  addButton: {
    height: '2.75rem',
    borderRadius: '.5rem',
  },
  content: {
    display: 'flex',
    gap: '16px',
    alignItems: 'center',
  },
  textField: {
    '& .MuiInputBase-input': {
      height: '0.8rem',
    },
  },

  variantImage: {
    cursor: 'pointer',
    border: `2px dashed ${colors.grey500}`,
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    opacity: 0.8,
    width: 48,
    height: 48,
    minWidth: 48,

    padding: 0,
    '& img': {
      maxWidth: '48px',
      maxHeight: '48px',
      width: '100%',
      height: '100%',
    },
  },
  existVariantImage: {
    border: 'none',
  },
  colorWrap: {
    width: 48,
    height: 48,
  },
  imageBox: {
    display: 'flex',
    height: 48,
    width: 48,
    minWidth: 48,
    justifyContent: 'center',
    alignSelf: 'center',
    alignItems: 'center',
    textAlign: 'center',
    position: 'relative',
    border: `2px dashed ${colors.grey500}`,
    borderRadius: '50%',
    overflow: 'hidden',
  },
  addImageBox: {
    opacity: 0.8,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '.25rem',

    '&:hover': {
      opacity: 1,
      backgroundColor: colors.grey200,
    },
  },
  existImageBox: {
    border: 'none',
    '& img': {
      width: '100%',
      height: '100%',
    },
  },
}));

const DEFAULT_VARIANT = {
  name: '',
};

export type CustomNewOptionValue = {
  imageId?: string,
  name: string,
  preview?: string,
  color?: string,
  file?: File,
};
export type CropImage = {
  file: File,
  preview: string,
  aspect?: number,
};

type Props = {
  variant?: ProductVariantOptionValue,
  onVariantSave: (variant: CustomNewOptionValue) => void,
  displayType?: string,
};

export default function EditVariantInput({
  variant,
  onVariantSave,
  displayType,
}: Props) {
  const classes = useStyles();

  const [openColorModal, setOpenColorModal] = useState<boolean>(false);
  const [imageToCrop, setImageToCrop] = useState<CropImage | null>(null);
  const [croppedImage, setCroppedImage] = useState<CropImage | null>(null);
  const [editVariant, setEditVariant] = useState(DEFAULT_VARIANT);

  const shouldSaveImage = displayType !== 'COLOR';

  const handleNameChange = e => {
    e.persist();
    setEditVariant(prev => ({...prev, name: e.target.value}));
  };

  const handleVariantInfo = () => {
    setOpenColorModal(true);
  };

  const handleSaveVariant = () => {
    const variantToSave = {
      ...editVariant,
      preview: croppedImage?.preview ?? undefined,
      file: croppedImage?.file ?? undefined,
    };

    if (shouldSaveImage) {
      setCroppedImage(null);
      onVariantSave(variantToSave);
    } else {
      onVariantSave({
        ...variantToSave,
        color: variantToSave.color,
      });
    }
    setEditVariant(DEFAULT_VARIANT);
  };

  const handleSelectColor = (color: string) =>
    setEditVariant(ps => ({...ps, color}));

  const handleUploadImage = async (
    e: SyntheticInputEvent<HTMLInputElement>
  ) => {
    const file = e.target.files[0];
    if (!file) return;

    setImageToCrop({file: file, preview: URL.createObjectURL(file)});
  };

  const handleKeyDown = e => {
    if (editVariant.name?.trim().length !== 0) {
      const {keyCode} = e;
      if (keyCode === 13) handleSaveVariant();
    }
  };

  return (
    <div className={classes.wrap}>
      <ColorOptionModal
        open={openColorModal}
        handleClose={() => setOpenColorModal(false)}
        onSelectColor={handleSelectColor}
      />
      {imageToCrop && (
        <CropModal
          open
          aspect={1}
          onClose={() => {
            setImageToCrop(null);
          }}
          images={[imageToCrop]}
          compressOptions={{
            quality: 0.8,
            maxWidth: 3840,
            maxHeight: 2160,
          }}
          keepFormat
          onSubmit={results => {
            if (results?.[0]) setCroppedImage(results[0]);
            setImageToCrop(null);
          }}
        />
      )}
      <div className={classes.content}>
        {shouldSaveImage ? (
          <label
            className={classNames(classes.imageBox, classes.addImageBox, {
              [classes.existImageBox]: !!croppedImage?.preview,
            })}
            aria-label="subir imagen"
          >
            <input
              hidden
              accept="image/png, image/jpeg"
              type="file"
              onChange={handleUploadImage}
            />

            {croppedImage?.preview ? (
              <img src={croppedImage?.preview} style={{width: '48px'}} alt="" />
            ) : (
              <AddPhotoAlternateOutlined color="disabled" />
            )}
          </label>
        ) : (
          <Button
            className={classNames(classes.variantImage, {
              [classes.existVariantImage]: !!editVariant.color || false,
            })}
            aria-label="seleccionar color"
            onClick={handleVariantInfo}
          >
            {editVariant?.color ? (
              <div
                className={classes.colorWrap}
                style={{backgroundColor: editVariant.color}}
              ></div>
            ) : (
              <ColorizeOutlined color="disabled" />
            )}
          </Button>
        )}

        <CustomTextField
          value={editVariant.name}
          placeholder="Nombre de la opción"
          onChange={handleNameChange}
          className={classes.textField}
          onKeyDown={handleKeyDown}
          aria-label="Entrada de texto para el nombre de la opción"
        />
        <Button
          disableElevation
          variant="contained"
          color="primary"
          className={classes.addButton}
          onClick={handleSaveVariant}
          aria-label="Guardar opción"
          disabled={editVariant.name?.trim().length <= 0}
        >
          <CheckIcon fontSize="small" />
        </Button>
      </div>
    </div>
  );
}
